import { Effect, Option } from 'effect';

import type {
  NotFoundError,
  ServerError,
  ViolationError,
} from '@vs-shop/engine/data/error/backendApi/_internal/createBackendApiError';
import { TokenRemovedRetryError } from '../../data/error/order';
import { isNewTokenRequiredViolationCode } from '@vs-shop/engine/data/error/orderViolation';
import { removeOrderShipment } from '../cookies/orderShipment';
import { removeOrderToken } from '../cookies/removeOrderToken';

/**
 * Handler function for ViolationError that checks for "new token required" violations
 * and cleans up cookies. Returns TokenRemovedRetryError if token was removed (signaling safe retry),
 * otherwise returns original error.
 *
 * Use with Effect.catchTag:
 *
 * @example
 * ```typescript
 * addProductVariantToOrderApi(token, data).pipe(
 *   Effect.catchTag(AddProductVariantError.Tags.Violation, removeTokenDueToViolation)
 * )
 * ```
 */
export const removeTokenDueToViolation = <E extends ViolationError>(error: E) =>
  Effect.gen(function* () {
    const shouldRemoveToken = Option.match(error.firstViolation, {
      // Empty violations = non-constraint 422 (e.g. "Cart with given token has not been found")
      onNone: () => true,
      onSome: (violation) => isNewTokenRequiredViolationCode(violation.code),
    });

    if (shouldRemoveToken) {
      yield* removeOrderToken;
      yield* removeOrderShipment;

      // Signal that token was removed - safe to retry
      return yield* new TokenRemovedRetryError({
        originalError: error,
      });
    }

    // Token wasn't removed - propagate original error (don't retry)
    return yield* error;
  });

export const removeTokenDueToServerError = <E extends ServerError>(error: E) =>
  Effect.gen(function* () {
    if (error.status === 500) {
      yield* removeOrderToken;
      yield* removeOrderShipment;

      // Signal that token was removed - safe to retry
      return yield* new TokenRemovedRetryError({
        originalError: error,
      });
    }

    // Token wasn't removed - propagate original error (don't retry)
    return yield* error;
  });

export const removeTokenDueToNotFound = (error: NotFoundError) =>
  Effect.gen(function* () {
    yield* removeOrderToken;
    yield* removeOrderShipment;

    // Signal that token was removed - safe to retry
    return yield* new TokenRemovedRetryError({
      originalError: error,
    });
  });
